import React, { useState, useEffect, useRef } from "react"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion"
import { Badge } from "../components/ui/badge"
import FooterLogos from "../components/FooterLogos";
import Container from "../components/Container";
import HTMLText from "../components/HTMLText";
import { getLocalizedContent } from "../literals/Literal";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import { badGyalBarcelonaContent as fanSaleContent } from "../data/badGyalBarcelonaSteps";

function InstructionsHero() {
  return (
    <section className="border-b border-gray-700">
      <div className="container mx-auto px-4 py-12 md:py-20 max-w-4xl">
        <div className="flex items-center gap-3 mb-8">
          <div className="text-4xl font-americaBold tracking-tight text-white">AccessTicket</div>
        </div>
        <h1 className="text-3xl sm:text-4xl md:text-6xl font-americaBold tracking-tight mb-6 text-white whitespace-nowrap">
          {getLocalizedContent(fanSaleContent.hero.title)}
        </h1>
        <p className="text-lg md:text-xl text-gray-300 max-w-2xl font-americaLight">
          {getLocalizedContent(fanSaleContent.hero.description)}
        </p>
      </div>
    </section>
  )
}

function InstructionsSteps() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [openStep, setOpenStep] = useState("");
  const stepRefs = useRef({});

  const handleImageClick = (imageUrl, imageAlt) => {
    setCurrentImage({ src: imageUrl, alt: imageAlt });
    setLightboxOpen(true);
  };

  // Handle URL parameters and scroll to step
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stepParam = urlParams.get('step');
    
    if (stepParam) {
      // Find the step that matches the parameter
      const matchingStep = fanSaleContent.steps.find(step =>
        step.id.includes(stepParam) || step.id === `step-${stepParam}`
      );
      
      if (matchingStep) {
        setOpenStep(matchingStep.id);
        
        // Scroll to the step after a short delay to ensure DOM is ready
        setTimeout(() => {
          const stepElement = stepRefs.current[matchingStep.id];
          if (stepElement) {
            stepElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest'
            });
          }
        }, 100);
      }
    }
  }, []);

  return (
    <section className="container mx-auto px-4 py-12 md:py-20 max-w-4xl">
      <Accordion type="single" collapsible className="space-y-4" value={openStep} onValueChange={setOpenStep}>
        {fanSaleContent.steps.map((step) => (
          <AccordionItem
            key={step.id}
            value={step.id}
            className="border border-gray-600 rounded-lg overflow-hidden"
            ref={(el) => stepRefs.current[step.id] = el}
            id={step.id}
          >
            <AccordionTrigger className="px-6 py-6 hover:no-underline hover:bg-gray-700 transition-colors [&[data-state=open]>div]:text-white">
              <div className="flex items-start gap-4 text-left w-full">
                <div className="bg-white text-black rounded px-2 py-1 text-xs font-mono font-medium shrink-0 mt-[7px]">
                  {step.number}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-americaBold text-white">{getLocalizedContent(step.title)}</h3>
                  <p className="text-base text-gray-300 font-americaLight">{getLocalizedContent(step.description)}</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
              <div className={step.fullWidth ? "pt-4" : "grid md:grid-cols-2 gap-8 pt-4"}>
                <div className="space-y-6">
                  <p className="text-base text-gray-100 leading-relaxed font-americaLight">{getLocalizedContent(step.content.intro)}</p>

                  {step.content.points && (
                    <ul className="space-y-2">
                      {getLocalizedContent(step.content.points).map((point, index) => (
                        <li key={index} className="flex gap-3 text-base">
                          <span className="text-gray-400 shrink-0 mt-1">•</span>
                          <HTMLText text={point} className="text-gray-200 leading-relaxed font-americaLight" />
                        </li>
                      ))}
                    </ul>
                  )}

                  {step.content.sections &&
                    step.content.sections.map((section, index) => (
                      <div key={index} className="space-y-3">
                        <h4 className="font-americaMedium text-gray-100 text-base">{getLocalizedContent(section.title)}</h4>
                        <ul className="space-y-2">
                          {getLocalizedContent(section.points).map((point, pointIndex) => (
                            <li key={pointIndex} className="flex gap-3 text-base">
                              <span className="text-gray-400 shrink-0 mt-1">•</span>
                              <HTMLText text={point} className="text-gray-200 leading-relaxed font-americaLight" />
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}

                </div>

                {!step.fullWidth && step.image && (
                  <div
                    className="relative aspect-[3/2] rounded-lg overflow-hidden bg-gray-700 border border-gray-600 flex items-center justify-center cursor-pointer hover:border-gray-500 transition-colors group"
                    onClick={() => handleImageClick(step.image, `${getLocalizedContent(step.title)} illustration`)}
                  >
                    <img
                      src={step.image}
                      alt={`${getLocalizedContent(step.title)} illustration`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white bg-opacity-20 rounded-full p-2">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                )}

                {step.fullWidth && step.image && (
                  <div className="mt-6">
                    <div
                      className="relative rounded-lg overflow-hidden bg-gray-700 border border-gray-600 flex items-center justify-center cursor-pointer hover:border-gray-500 transition-colors group"
                      onClick={() => handleImageClick(step.image, `${getLocalizedContent(step.title)} illustration`)}
                    >
                      <img
                        src={step.image}
                        alt={`${getLocalizedContent(step.title)} illustration`}
                        className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-200"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white bg-opacity-20 rounded-full p-2">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              </div>

              {step.content.warning && (
                <div className="bg-gray-700 border border-gray-600 rounded-lg p-4 space-y-3 mt-6">
                  <h4 className="font-americaMedium text-gray-100 text-base">{getLocalizedContent(step.content.warning)}</h4>
                  {step.content.warningDetails && (
                    <ul className="space-y-2">
                      {getLocalizedContent(step.content.warningDetails).map((detail, index) => (
                        <li key={index} className="flex gap-3 text-base">
                          <span className="text-gray-400 shrink-0 mt-1">•</span>
                          <HTMLText text={detail} className="text-gray-200 leading-relaxed font-americaLight" />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {step.content.tip && (
                <div className="bg-blue-900 border border-blue-700 rounded-lg p-4 mt-6">
                  <p className="text-base font-americaLight">
                    <span className="text-blue-300 font-americaMedium">Pro Tip:</span>{" "}
                    <span className="text-gray-100">{getLocalizedContent(step.content.tip)}</span>
                  </p>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {currentImage && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          slides={[currentImage]}
          plugins={[Zoom]}
          zoom={{
            maxZoomPixelRatio: 3,
            zoomInMultiplier: 2,
            doubleTapDelay: 300,
            doubleClickDelay: 300,
            scrollToZoom: true
          }}
          render={{
            buttonPrev: () => null,
            buttonNext: () => null,
          }}
        />
      )}
    </section>
  )
}

const BadGyalBarcelona = () => {
  return (
    <Container>
      <InstructionsHero />
      <InstructionsSteps />
      <FooterLogos />
    </Container>
  )
}

export default BadGyalBarcelona
